import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useAnalysisTestSelectionStore } from '@/store/useAnalysisTestSelectionStore';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Slider,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ScienceIcon from '@mui/icons-material/Science';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ReferenceLine,
  ReferenceArea,
  ResponsiveContainer,
} from 'recharts';
import { useUser } from '@/store/useAuthStore';
import {
  classificationService,
  type CalibrationRecommendation,
  type ChannelConfigurationDto,
  type ExcludedCalibrationTest,
  type ThresholdRecommendation,
  activeThresholdKey,
  SIGNAL_METHOD_LABELS,
  SIGNAL_METHOD_UNITS,
} from '@/services/api/classificationService';
import { ActiveConfigurationPanel } from './ActiveConfigurationPanel';
import { CHANNEL_COLOR_MAP } from './Charts/CombinedPlot';
import { buildMetricsReportHtml, describeExclusion, formatTestName } from './metricsReport';

// Channels a calibration can target — the same four the charts plot and colour.
const CALIBRATION_CHANNELS = [1, 2, 3, 4];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// Checkbox rows that map calibration recommendations to config fields.
// apply() returns ONLY the fields to change — never spreads the full draft.
// This ensures multiple checked rows all accumulate correctly.
interface RecommendationRow {
  key: string;
  label: string;
  apply: (rec: ThresholdRecommendation) => Partial<ChannelConfigurationDto>;
  value?: (rec: ThresholdRecommendation) => string;
}

function activeThresholdFromRec(rec: ThresholdRecommendation, method: string): number {
  switch (method) {
    case 'NyquistArea': return rec.thresholdNyquistArea;
    case 'FreqArea': return rec.thresholdFreqArea;
    case 'AscendingArcArea': return rec.thresholdAscendingArcArea;
    case 'MaxImaginaryInterpolated': return rec.thresholdMaxImaginaryInterpolated;
    case 'MaxImaginaryAbsolute': return rec.thresholdMaxImaginaryAbsolute;
    default: return rec.classificationThreshold;
  }
}

function buildRecommendationRows(activeMethod: string): RecommendationRow[] {
  const threshLabel = `Classification Threshold (${SIGNAL_METHOD_LABELS[activeMethod] ?? activeMethod})`;
  const threshKey = activeThresholdKey(activeMethod);

  return [
    {
      key: 'threshold',
      label: threshLabel,
      apply: (rec) => ({ [threshKey]: activeThresholdFromRec(rec, activeMethod) } as Partial<ChannelConfigurationDto>),
      value: (rec) => `${activeThresholdFromRec(rec, activeMethod).toFixed(2)} ${SIGNAL_METHOD_UNITS[activeMethod] ?? '%'}`,
    },
    {
      key: 'curveRadius',
      label: 'Curve Radius',
      apply: (rec) => ({ curveRadiusMin: rec.curveRadiusMin, curveRadiusMax: rec.curveRadiusMax }),
      value: (rec) => `${rec.curveRadiusMin.toFixed(1)} – ${rec.curveRadiusMax.toFixed(1)} Ω`,
    },
    {
      key: 'impedanceRange',
      label: 'Impedance Range',
      apply: (rec) => ({ maxImaginaryMin: rec.maxImaginaryMin, maxImaginaryMax: rec.maxImaginaryMax }),
      value: (rec) => `${rec.maxImaginaryMin.toFixed(1)} – ${rec.maxImaginaryMax.toFixed(1)} Ω`,
    },
    {
      key: 'rsDrift',
      label: 'Rs Drift Limit',
      apply: (rec) => ({ rsDriftLimit: rec.rsDriftLimit }),
      value: (rec) => `${rec.rsDriftLimit.toFixed(1)} Ω`,
    },
    {
      key: 'slopeReversalSensitivity',
      label: 'Slope Reversal Sensitivity',
      apply: (rec) => ({ slopeReversalSensitivity: rec.slopeReversalSensitivity ?? 0.05 }),
      value: (rec) => (rec.slopeReversalSensitivity ?? 0.05).toFixed(4),
    },
    {
      key: 'inflectionSensitivity',
      label: 'Inflection Point Sensitivity',
      apply: (rec) => ({ inflectionPointSensitivity: rec.inflectionPointSensitivity }),
      value: (rec) => rec.inflectionPointSensitivity.toFixed(2),
    },
  ];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface HistogramBin {
  bin: string;
  count: number;
  rangeLow: number;
  rangeHigh: number;
  isMinBin: boolean;
  isMaxBin: boolean;
}

// Formats a number to a fixed number of decimals, normalizing the
// "-0.00"-style negative-zero artifact that `toFixed` produces both for a
// literal -0 and for any tiny negative float that rounds to zero at the
// given precision (e.g. -3.8e-11.toFixed(2) === "-0.00"). Real ΔSignal
// metrics with negative, non-round `min` bounds can land bin boundaries
// (`min + step * i`) on exactly such values due to floating-point rounding.
function toFixedNoNegZero(x: number, digits = 2): string {
  const fixed = x.toFixed(digits);
  return fixed.startsWith('-') && Number(fixed) === 0 ? fixed.slice(1) : fixed;
}

// eslint-disable-next-line react-refresh/only-export-components -- exported for direct unit testing (Story 1.5, Task 2)
export function buildHistogramData(rawValues: number[], min: number, max: number): HistogramBin[] {
  if (rawValues.length === 0) return [];
  if (max === min) {
    return [{
      bin: toFixedNoNegZero(rawValues[0]),
      count: rawValues.length,
      rangeLow: rawValues[0],
      rangeHigh: rawValues[0],
      isMinBin: true,
      isMaxBin: true,
    }];
  }

  const bins = 10;
  const step = (max - min) / bins;
  const counts = new Array(bins).fill(0);
  for (const v of rawValues) {
    if (!Number.isFinite(v)) continue;
    const index = Math.max(0, Math.min(bins - 1, Math.floor((v - min) / step)));
    counts[index] += 1;
  }

  return counts.map((count, i) => {
    const binCenter = min + step * (i + 0.5);
    return {
      bin: toFixedNoNegZero(binCenter),
      count,
      rangeLow: min + step * i,
      rangeHigh: i === bins - 1 ? max : min + step * (i + 1),
      isMinBin: i === 0,
      isMaxBin: i === bins - 1,
    };
  });
}

function applyMargin(
  rec: ThresholdRecommendation,
  origMean: Record<string, number>,
  origStdDev: Record<string, number>,
  margin: number,
): ThresholdRecommendation {
  const deltaThreshold = (key: string, fallback: number) =>
    origMean[key] !== undefined
      ? Math.max(1.0, origMean[key] + margin * origStdDev[key])
      : fallback;

  return {
    curveRadiusMin: Math.max(0, origMean.curveRadius - margin * origStdDev.curveRadius),
    curveRadiusMax: origMean.curveRadius + margin * origStdDev.curveRadius,
    maxImaginaryMin: Math.max(0, origMean.maxImaginary - margin * origStdDev.maxImaginary),
    maxImaginaryMax: origMean.maxImaginary + margin * origStdDev.maxImaginary,
    rsAt10kHzMin: Math.max(0, origMean.rsAt10kHz - margin * origStdDev.rsAt10kHz),
    rsAt10kHzMax: origMean.rsAt10kHz + margin * origStdDev.rsAt10kHz,
    rsDriftLimit: origMean.rsDrift + margin * origStdDev.rsDrift,
    classificationThreshold: deltaThreshold('deltaSignal', rec.classificationThreshold),
    thresholdNyquistArea: deltaThreshold('deltaNyquistArea', rec.thresholdNyquistArea),
    thresholdFreqArea: deltaThreshold('deltaFreqArea', rec.thresholdFreqArea),
    thresholdAscendingArcArea: deltaThreshold('deltaAscendingArcArea', rec.thresholdAscendingArcArea),
    thresholdMaxImaginaryInterpolated: deltaThreshold('deltaMaxImaginaryInterpolated', rec.thresholdMaxImaginaryInterpolated),
    thresholdMaxImaginaryAbsolute: deltaThreshold('deltaMaxImaginaryAbsolute', rec.thresholdMaxImaginaryAbsolute),
    inflectionPointSensitivity: rec.inflectionPointSensitivity,
    slopeReversalSensitivity: rec.slopeReversalSensitivity,
  };
}

// ---------------------------------------------------------------------------
// Default config (used while loading or when no channel selected)
// ---------------------------------------------------------------------------

function defaultConfig(channelId: number): ChannelConfigurationDto {
  return {
    id: 0,
    channelId,
    classificationThreshold: 10.0,
    curveRadiusMin: 50.0,
    curveRadiusMax: 500.0,
    maxImaginaryMin: 100.0,
    maxImaginaryMax: 5000.0,
    rsDriftLimit: 200.0,
    inflectionPointSensitivity: 4.0,
    slopeReversalSensitivity: 0.05,
    hookDetectionTolerance: 50.0,
    signalExtractionMethod: 'MaxImaginary',
    thresholdNyquistArea: 10.0,
    thresholdFreqArea: 10.0,
    thresholdAscendingArcArea: 10.0,
    thresholdMaxImaginaryInterpolated: 10.0,
    thresholdMaxImaginaryAbsolute: 50.0,
    lowerThresholdMaxImaginary: 0.0,
    lowerThresholdNyquistArea: 0.0,
    lowerThresholdFreqArea: 0.0,
    lowerThresholdAscendingArcArea: 0.0,
    lowerThresholdMaxImaginaryInterpolated: 0.0,
    lowerThresholdMaxImaginaryAbsolute: 0.0,
    disabledAnomalyChecks: [],
  };
}

// Field-by-field comparison, so key order and array identity don't register as edits.
function configsEqual(a: ChannelConfigurationDto, b: ChannelConfigurationDto): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]) as Set<keyof ChannelConfigurationDto>;
  for (const key of keys) {
    if (JSON.stringify(a[key]) !== JSON.stringify(b[key])) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Histogram sub-component
// ---------------------------------------------------------------------------

// eslint-disable-next-line react-refresh/only-export-components -- exported for direct unit testing (Story 1.5, Task 3)
export function formatBinTooltip(bin: HistogramBin, unit: string): string {
  const unitSuffix = unit ? ` ${unit}` : '';
  const edgeSuffix = bin.isMinBin && bin.isMaxBin ? '' : bin.isMinBin ? ' (min)' : bin.isMaxBin ? ' (max)' : '';
  return `${toFixedNoNegZero(bin.rangeLow)} – ${toFixedNoNegZero(bin.rangeHigh)}${unitSuffix}${edgeSuffix} · n=${bin.count}`;
}

export function HistogramTooltipContent({ active, payload, unit }: {
  active?: boolean;
  payload?: ReadonlyArray<{ payload: HistogramBin }>;
  unit: string;
}) {
  if (!active || !payload?.length || !payload[0]?.payload) return null;
  return (
    <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', p: 0.75, borderRadius: 1 }}>
      <Typography variant="caption">{formatBinTooltip(payload[0].payload, unit)}</Typography>
    </Box>
  );
}

function MetricHistogram({ label, mean, min, max, boundsMin, boundsMax, rawValues }: {
  label: string; mean: number; min: number; max: number;
  boundsMin: number; boundsMax: number; rawValues: number[];
}) {
  const data = buildHistogramData(rawValues, min, max);
  const unit = label.match(/\(([^()]+)\)\s*$/)?.[1] ?? '';
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <ResponsiveContainer width="100%" height={120}>
        <BarChart data={data} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="bin" tick={{ fontSize: 9 }} />
          <YAxis tick={{ fontSize: 9 }} />
          <RechartsTooltip content={(props) => <HistogramTooltipContent {...props} unit={unit} />} />
          <Bar dataKey="count" fill="#90caf9" />
          <ReferenceLine x={mean.toFixed(2)} stroke="#1565c0" strokeWidth={2} label={{ value: 'μ', fontSize: 10 }} />
          <ReferenceArea x1={boundsMin.toFixed(2)} x2={boundsMax.toFixed(2)} fill="#1565c0" fillOpacity={0.1} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Excluded tests notice
// ---------------------------------------------------------------------------

function ExcludedTestsNotice({ excluded }: { excluded: ExcludedCalibrationTest[] }) {
  const [open, setOpen] = useState(false);
  if (excluded.length === 0) return null;
  return (
    <Alert
      severity="warning"
      action={
        <Button color="inherit" size="small" onClick={() => setOpen((o) => !o)}>
          {open ? 'Hide' : 'Show'}
        </Button>
      }
    >
      {excluded.length} ticked test{excluded.length !== 1 ? 's were' : ' was'} left out of the analysis.
      <Collapse in={open} unmountOnExit>
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
          {excluded.map((test) => (
            <li key={test.testId}>
              <strong>{formatTestName(test)}</strong> — {describeExclusion(test)}
            </li>
          ))}
        </Box>
      </Collapse>
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ClassificationCalibrationTool() {
  const tickedIds = useAnalysisTestSelectionStore((s) => s.tickedIds);
  const selectedIds = useMemo(() => new Set(tickedIds), [tickedIds]);
  // The channel scopes the whole tab: the loaded config, Analyze, Apply and Save all target it.
  const [channel, setChannel] = useState(CALIBRATION_CHANNELS[0]);
  // Kept after the prompt closes so its text doesn't change during the exit transition.
  const [pendingSwitch, setPendingSwitch] = useState<{ from: number; to: number } | null>(null);
  const [discardPromptOpen, setDiscardPromptOpen] = useState(false);

  // Panel 1 state
  const [draftConfig, setDraftConfig] = useState<ChannelConfigurationDto>(defaultConfig(CALIBRATION_CHANNELS[0]));
  const [storedConfig, setStoredConfig] = useState<ChannelConfigurationDto | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Panel 2 state
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState<CalibrationRecommendation | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [safetyMargin, setSafetyMargin] = useState(2.5);
  const [overrideEnabled, setOverrideEnabled] = useState<Record<string, boolean>>({});
  const [manualOverrides, setManualOverrides] = useState<Record<string, number>>({});
  const [checkedRows, setCheckedRows] = useState<Record<string, boolean>>({});
  const [appliedNotice, setAppliedNotice] = useState(false);

  // Load config from backend when the channel changes; a stale response from a
  // previously selected channel is dropped.
  useEffect(() => {
    let cancelled = false;
    setConfigLoading(true);
    setConfigError(null);
    classificationService.getConfiguration(channel)
      .then((cfg) => {
        if (cancelled) return;
        setStoredConfig(cfg);
        setDraftConfig(cfg);
      })
      .catch((err) => {
        if (cancelled) return;
        setConfigError(err instanceof Error ? err.message : 'Failed to load configuration');
        // Nothing stored to compare against — the defaults become the unsaved-changes baseline.
        const fallback = defaultConfig(channel);
        setStoredConfig(fallback);
        setDraftConfig(fallback);
      })
      .finally(() => {
        if (!cancelled) setConfigLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [channel]);

  const isDirty = storedConfig !== null && !configsEqual(draftConfig, storedConfig);

  const clearAnalysis = useCallback(() => {
    setResult(null);
    setAnalysisError(null);
    setOverrideEnabled({});
    setManualOverrides({});
    setCheckedRows({});
    setAppliedNotice(false);
  }, []);

  // Channel handlers — results belong to the channel they were calculated for,
  // so they are cleared rather than carried over to the next channel.
  const switchChannel = useCallback((next: number) => {
    setDiscardPromptOpen(false);
    setSaveError(null);
    setStoredConfig(null); // no baseline until the next channel's config arrives
    clearAnalysis();
    setChannel(next);
  }, [clearAnalysis]);

  const handleChannelChange = useCallback((_: unknown, next: number | null) => {
    if (next === null || next === channel) return;
    if (isDirty) {
      setPendingSwitch({ from: channel, to: next });
      setDiscardPromptOpen(true);
    } else {
      switchChannel(next);
    }
  }, [channel, isDirty, switchChannel]);

  // Panel 1 handlers
  const handleConfigChange = useCallback((updates: Partial<ChannelConfigurationDto>) => {
    setDraftConfig((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleSave = useCallback(async () => {
    setSaveError(null);
    setSaving(true);
    try {
      const resp = await classificationService.saveConfiguration({
        channelId: channel,
        classificationThreshold: draftConfig.classificationThreshold,
        curveRadiusMin: draftConfig.curveRadiusMin,
        curveRadiusMax: draftConfig.curveRadiusMax,
        maxImaginaryMin: draftConfig.maxImaginaryMin,
        maxImaginaryMax: draftConfig.maxImaginaryMax,
        rsDriftLimit: draftConfig.rsDriftLimit,
        inflectionPointSensitivity: draftConfig.inflectionPointSensitivity,
        slopeReversalSensitivity: draftConfig.slopeReversalSensitivity,
        hookDetectionTolerance: draftConfig.hookDetectionTolerance,
        signalExtractionMethod: draftConfig.signalExtractionMethod,
        thresholdNyquistArea: draftConfig.thresholdNyquistArea,
        thresholdFreqArea: draftConfig.thresholdFreqArea,
        thresholdAscendingArcArea: draftConfig.thresholdAscendingArcArea,
        thresholdMaxImaginaryInterpolated: draftConfig.thresholdMaxImaginaryInterpolated,
        thresholdMaxImaginaryAbsolute: draftConfig.thresholdMaxImaginaryAbsolute,
        lowerThresholdMaxImaginary: draftConfig.lowerThresholdMaxImaginary,
        lowerThresholdNyquistArea: draftConfig.lowerThresholdNyquistArea,
        lowerThresholdFreqArea: draftConfig.lowerThresholdFreqArea,
        lowerThresholdAscendingArcArea: draftConfig.lowerThresholdAscendingArcArea,
        lowerThresholdMaxImaginaryInterpolated: draftConfig.lowerThresholdMaxImaginaryInterpolated,
        lowerThresholdMaxImaginaryAbsolute: draftConfig.lowerThresholdMaxImaginaryAbsolute,
        notes: draftConfig.notes,
        disabledAnomalyChecks: draftConfig.disabledAnomalyChecks,
      });
      setStoredConfig(resp.configuration);
      setDraftConfig(resp.configuration);
      setSuccessMsg(`CH${channel} configuration saved. Classification Service will use it immediately (no restart required).`);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [channel, draftConfig]);

  // Panel 2 handlers
  const handleAnalyze = useCallback(async () => {
    clearAnalysis();
    setAnalyzing(true);
    try {
      const resp = await classificationService.calibrate({
        // Sent as ticked: the backend decides eligibility and reports what it left out.
        testIds: Array.from(selectedIds),
        channelId: channel,
      });
      setResult(resp.recommendation);
      // Default all checkboxes to checked
      const rows = buildRecommendationRows(draftConfig.signalExtractionMethod);
      setCheckedRows(Object.fromEntries(rows.map((r) => [r.key, true])));
    } catch (err) {
      setAnalysisError(err instanceof Error ? err.message : 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  }, [clearAnalysis, selectedIds, channel, draftConfig.signalExtractionMethod]);

  // The channel bar's Analyze opens the assistant and brings it into view, so the
  // spinner, errors and results are never left off-screen in a collapsed section.
  const channelBarRef = useRef<HTMLDivElement>(null);
  const assistantRef = useRef<HTMLElement>(null);
  const scrollOnExpandRef = useRef(false);

  const scrollAssistantIntoView = useCallback(() => {
    const section = assistantRef.current;
    if (!section) return;
    // Stop below the sticky channel bar so the section header isn't hidden behind it.
    section.style.scrollMarginTop = `${(channelBarRef.current?.offsetHeight ?? 0) + 16}px`;
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleAnalyzeFromBar = useCallback(() => {
    if (assistantOpen) {
      scrollAssistantIntoView();
    } else {
      // Scroll once the Collapse has expanded, so there is room to bring the header to the top.
      scrollOnExpandRef.current = true;
      setAssistantOpen(true);
    }
    void handleAnalyze();
  }, [assistantOpen, scrollAssistantIntoView, handleAnalyze]);

  const handleAssistantEntered = useCallback(() => {
    if (!scrollOnExpandRef.current) return;
    scrollOnExpandRef.current = false;
    scrollAssistantIntoView();
  }, [scrollAssistantIntoView]);

  // All-channel report: read-only, opens in a new tab, never touches a configuration.
  const user = useUser();
  const [reportGenerating, setReportGenerating] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  // Kept until the next report, so the open tab can still be saved or printed.
  const reportUrlRef = useRef<string | null>(null);

  const handleOpenReport = useCallback(async () => {
    setReportError(null);
    // Open the tab within the click: browsers block tabs opened after an await.
    const tab = window.open('', '_blank');
    if (!tab) {
      setReportError('The browser blocked the report tab. Allow pop-ups for LabCloud and try again.');
      return;
    }
    tab.opener = null;
    tab.document.title = 'Calibration metrics report';
    tab.document.body.textContent = 'Generating report…';

    setReportGenerating(true);
    try {
      const report = await classificationService.metricsReport(Array.from(selectedIds));
      const html = buildMetricsReportHtml(report, { generatedBy: user?.fullName || user?.email || 'Unknown user' });
      if (reportUrlRef.current) URL.revokeObjectURL(reportUrlRef.current);
      reportUrlRef.current = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
      tab.location.href = reportUrlRef.current;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Report failed';
      tab.document.body.textContent = `The report could not be generated: ${message}`;
      setReportError(`The report could not be generated: ${message}`);
    } finally {
      setReportGenerating(false);
    }
  }, [selectedIds, user]);

  const effectiveThresholds = useCallback((): ThresholdRecommendation | null => {
    if (!result) return null;
    const base = applyMargin(
      result.recommendations,
      {
        curveRadius: result.distributions.curveRadius.mean,
        maxImaginary: result.distributions.maxImaginary.mean,
        rsAt10kHz: result.distributions.rsAt10kHz.mean,
        rsDrift: result.distributions.rsDrift.mean,
        deltaSignal: result.distributions.deltaSignal.mean,
        deltaNyquistArea: result.distributions.deltaNyquistArea.mean,
        deltaFreqArea: result.distributions.deltaFreqArea.mean,
        deltaAscendingArcArea: result.distributions.deltaAscendingArcArea.mean,
        deltaMaxImaginaryInterpolated: result.distributions.deltaMaxImaginaryInterpolated.mean,
        deltaMaxImaginaryAbsolute: result.distributions.deltaMaxImaginaryAbsolute.mean,
      },
      {
        curveRadius: result.distributions.curveRadius.stdDev,
        maxImaginary: result.distributions.maxImaginary.stdDev,
        rsAt10kHz: result.distributions.rsAt10kHz.stdDev,
        rsDrift: result.distributions.rsDrift.stdDev,
        deltaSignal: result.distributions.deltaSignal.stdDev,
        deltaNyquistArea: result.distributions.deltaNyquistArea.stdDev,
        deltaFreqArea: result.distributions.deltaFreqArea.stdDev,
        deltaAscendingArcArea: result.distributions.deltaAscendingArcArea.stdDev,
        deltaMaxImaginaryInterpolated: result.distributions.deltaMaxImaginaryInterpolated.stdDev,
        deltaMaxImaginaryAbsolute: result.distributions.deltaMaxImaginaryAbsolute.stdDev,
      },
      safetyMargin,
    );
    const ov = (key: string, val: number) =>
      overrideEnabled[key] ? (manualOverrides[key] ?? val) : val;

    return {
      curveRadiusMin: ov('curveRadiusMin', base.curveRadiusMin),
      curveRadiusMax: ov('curveRadiusMax', base.curveRadiusMax),
      maxImaginaryMin: ov('maxImaginaryMin', base.maxImaginaryMin),
      maxImaginaryMax: ov('maxImaginaryMax', base.maxImaginaryMax),
      rsAt10kHzMin: ov('rsAt10kHzMin', base.rsAt10kHzMin),
      rsAt10kHzMax: ov('rsAt10kHzMax', base.rsAt10kHzMax),
      rsDriftLimit: ov('rsDriftLimit', base.rsDriftLimit),
      classificationThreshold: ov('threshold', base.classificationThreshold),
      thresholdNyquistArea: ov('threshold', base.thresholdNyquistArea),
      thresholdFreqArea: ov('threshold', base.thresholdFreqArea),
      thresholdAscendingArcArea: ov('threshold', base.thresholdAscendingArcArea),
      thresholdMaxImaginaryInterpolated: ov('threshold', base.thresholdMaxImaginaryInterpolated),
      thresholdMaxImaginaryAbsolute: ov('threshold', base.thresholdMaxImaginaryAbsolute),
      inflectionPointSensitivity: ov('inflectionSensitivity', base.inflectionPointSensitivity),
      slopeReversalSensitivity: ov('slopeReversalSensitivity', base.slopeReversalSensitivity),
    };
  }, [result, safetyMargin, overrideEnabled, manualOverrides]);

  // Backstop: never copy one channel's recommendations into another channel's config.
  const resultMatchesChannel = result !== null && result.channelId === channel;

  const handleApplySelected = useCallback(() => {
    if (!resultMatchesChannel) return;
    const thresholds = effectiveThresholds();
    if (!thresholds) return;

    const anyChecked = Object.values(checkedRows).some(Boolean);
    if (!anyChecked) return;

    const rows = buildRecommendationRows(draftConfig.signalExtractionMethod);
    let updates: Partial<ChannelConfigurationDto> = {};
    for (const row of rows) {
      if (checkedRows[row.key]) {
        updates = { ...updates, ...row.apply(thresholds) };
      }
    }
    setDraftConfig((prev) => ({ ...prev, ...updates }));
    setAppliedNotice(true);
  }, [resultMatchesChannel, effectiveThresholds, checkedRows, draftConfig]);

  const thresholds = effectiveThresholds();
  const recommendationRows = result ? buildRecommendationRows(draftConfig.signalExtractionMethod) : [];
  const anyRowChecked = Object.values(checkedRows).some(Boolean);

  // Shared by both Analyze buttons so they always read and behave as one action.
  const analyzeDisabled = selectedIds.size === 0 || analyzing;
  const analyzeIcon = analyzing ? <CircularProgress size={16} color="inherit" /> : <ScienceIcon />;
  const analyzeLabel = analyzing
    ? `Analyzing CH${channel}…`
    : `Analyze CH${channel}${selectedIds.size > 0 ? ` (${selectedIds.size} tests)` : ''}`;
  const signalMethodLabel = SIGNAL_METHOD_LABELS[draftConfig.signalExtractionMethod] ?? draftConfig.signalExtractionMethod;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* Channel scope — sticky so the target channel stays visible while scrolling to Analyze/Apply */}
      <Paper
        ref={channelBarRef}
        role="toolbar"
        aria-labelledby="calibration-channel-label"
        variant="outlined"
        sx={{ position: 'sticky', top: 0, zIndex: 2, p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}
      >
        <Typography id="calibration-channel-label" variant="subtitle2" sx={{ flexShrink: 0 }}>
          Calibrating channel
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={channel}
          onChange={handleChannelChange}
          disabled={analyzing || saving}
          aria-labelledby="calibration-channel-label"
        >
          {CALIBRATION_CHANNELS.map((ch) => (
            <ToggleButton key={ch} value={ch} sx={{ px: 1.5, gap: 0.75 }}>
              <Box
                component="span"
                sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: CHANNEL_COLOR_MAP.get(`CH${ch}`) ?? 'grey.500' }}
              />
              CH{ch}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Tooltip
          title={selectedIds.size === 0
            ? 'Tick measurements in the left pane to analyze them'
            : `Calibrating threshold for: ${signalMethodLabel}`}
        >
          <span>
            <Button variant="contained" disabled={analyzeDisabled} startIcon={analyzeIcon} onClick={handleAnalyzeFromBar}>
              {analyzeLabel}
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          title={selectedIds.size === 0
            ? 'Tick measurements in the left pane to report on them'
            : 'Every metric for every channel of the ticked tests, in a new tab. Read-only.'}
        >
          <span>
            <Button
              variant="outlined"
              disabled={selectedIds.size === 0 || reportGenerating}
              endIcon={reportGenerating ? <CircularProgress size={16} color="inherit" /> : <OpenInNewIcon />}
              onClick={() => void handleOpenReport()}
            >
              {reportGenerating ? 'Generating report…' : 'All-channel report'}
            </Button>
          </span>
        </Tooltip>
        {isDirty &&<Chip label="Unsaved changes" size="small" color="warning" variant="outlined" />}
        {selectedIds.size > 0 && (
          <Typography variant="caption" color="text.secondary">
            {selectedIds.size} test{selectedIds.size !== 1 ? 's' : ''} selected
          </Typography>
        )}
      </Paper>
      {reportError && (
        <Alert severity="error" onClose={() => setReportError(null)}>{reportError}</Alert>
      )}

        {/* Panel 1 — Active Configuration */}
        {configLoading && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', color: 'text.secondary' }}>
            <CircularProgress size={16} />
            <Typography variant="body2">Loading configuration…</Typography>
          </Box>
        )}
        {configError && <Alert severity="warning">{configError}</Alert>}
        {saveError && <Alert severity="error">{saveError}</Alert>}

        {!configLoading && (
          <ActiveConfigurationPanel
            config={draftConfig}
            onChange={handleConfigChange}
            onSave={handleSave}
            saving={saving}
          />
        )}

        {/* Panel 2 — Calibration Assistant (collapsible) */}
        <Paper ref={assistantRef} component="section" aria-labelledby="calibration-assistant-title" variant="outlined">
          <Box
            sx={{ p: 1.5, display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
            onClick={() => setAssistantOpen((o) => !o)}
          >
            <Typography id="calibration-assistant-title" variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1 }}>
              Calibration Assistant — CH{channel}
            </Typography>
            {assistantOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>

          <Collapse in={assistantOpen} onEntered={handleAssistantEntered}>
            <Divider />
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

              {/* Analyze button — the re-run counterpart of the channel bar's primary one */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button variant="outlined" disabled={analyzeDisabled} startIcon={analyzeIcon} onClick={handleAnalyze}>
                  {analyzeLabel}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Calibrating threshold for: <strong>{signalMethodLabel}</strong>
                </Typography>
              </Box>

              {analysisError && (
                <Alert severity="error" action={<Button size="small" onClick={handleAnalyze}>Retry</Button>}>
                  {analysisError}
                </Alert>
              )}

              {result && (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip label={`Results for CH${result.channelId}`} size="small" color="primary" />
                  <Chip label={`${result.numTestsAnalyzed} of ${result.numTestsRequested} tests analyzed`} size="small" />
                  <Chip label={new Date(result.analysisTimestamp).toLocaleString()} size="small" variant="outlined" />
                </Stack>
              )}
              {result && <ExcludedTestsNotice excluded={result.excludedTests} />}
              {result && result.numTestsAnalyzed === 0 && (
                <Alert severity="error">
                  None of the ticked tests had usable data for CH{result.channelId}, so there are no recommendations.
                </Alert>
              )}

              {result && result.numTestsAnalyzed > 0 && (
                <>

                  {/* Safety margin */}
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Safety Margin</Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="body2" color="text.secondary" sx={{ width: 30 }}>2.0</Typography>
                      <Slider
                        min={2.0} max={3.5} step={0.1} value={safetyMargin}
                        onChange={(_, v) => setSafetyMargin(v as number)}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${v} SD`}
                        sx={{ flexGrow: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ width: 30 }}>3.5</Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">Current: mean ± {safetyMargin} SD</Typography>
                  </Paper>

                  {/* Recommendations table with checkboxes */}
                  <Paper variant="outlined">
                    <Typography variant="subtitle2" sx={{ p: 2, pb: 0 }}>Recommendations</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" />
                          <TableCell>Parameter</TableCell>
                          <TableCell align="right">Recommended Value</TableCell>
                          <TableCell>Override</TableCell>
                          <TableCell>Custom</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recommendationRows.map((row) => (
                          <TableRow key={row.key}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                checked={!!checkedRows[row.key]}
                                onChange={(e) => setCheckedRows((prev) => ({ ...prev, [row.key]: e.target.checked }))}
                              />
                            </TableCell>
                            <TableCell>{row.label}</TableCell>
                            <TableCell align="right">
                              <Typography variant="body2">{thresholds ? row.value?.(thresholds) : '—'}</Typography>
                            </TableCell>
                            <TableCell padding="checkbox">
                              <Tooltip title={`Override ${row.label}`}>
                                <Switch
                                  size="small"
                                  checked={!!overrideEnabled[row.key]}
                                  onChange={(e) => setOverrideEnabled((prev) => ({ ...prev, [row.key]: e.target.checked }))}
                                />
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              {overrideEnabled[row.key] && thresholds && (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={manualOverrides[row.key] ?? row.value?.(thresholds) ?? ''}
                                  onChange={(e) => {
                                    const v = parseFloat(e.target.value);
                                    if (!isNaN(v)) setManualOverrides((prev) => ({ ...prev, [row.key]: v }));
                                  }}
                                  sx={{ width: 120 }}
                                  inputProps={{ step: 0.001 }}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>

                  {/* Distribution charts */}
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Distributions</Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      {[
                        { d: result.distributions.curveRadius, label: 'Curve Radius (Ω)', bMin: thresholds?.curveRadiusMin ?? 0, bMax: thresholds?.curveRadiusMax ?? 0 },
                        { d: result.distributions.maxImaginary, label: 'Max Imaginary (Ω)', bMin: thresholds?.maxImaginaryMin ?? 0, bMax: thresholds?.maxImaginaryMax ?? 0 },
                        { d: result.distributions.rsAt10kHz, label: 'Rs @ 10kHz (Ω)', bMin: thresholds?.rsAt10kHzMin ?? 0, bMax: thresholds?.rsAt10kHzMax ?? 0 },
                      ].map(({ d, label, bMin, bMax }) => (
                        <Box key={label} sx={{ minWidth: 220, flex: 1 }}>
                          <MetricHistogram label={label} mean={d.mean} min={d.min} max={d.max} boundsMin={bMin} boundsMax={bMax} rawValues={d.rawValues} />
                        </Box>
                      ))}
                    </Stack>
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>ΔSignal Distributions</Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      {[
                        { d: result.distributions.deltaSignal, label: `ΔMax Imaginary (${SIGNAL_METHOD_UNITS.MaxImaginary})`, bound: thresholds?.classificationThreshold ?? 0 },
                        { d: result.distributions.deltaNyquistArea, label: `ΔNyquist Area (${SIGNAL_METHOD_UNITS.NyquistArea})`, bound: thresholds?.thresholdNyquistArea ?? 0 },
                        { d: result.distributions.deltaFreqArea, label: `ΔFrequency Area (${SIGNAL_METHOD_UNITS.FreqArea})`, bound: thresholds?.thresholdFreqArea ?? 0 },
                        { d: result.distributions.deltaAscendingArcArea, label: `ΔAscending Arc (${SIGNAL_METHOD_UNITS.AscendingArcArea})`, bound: thresholds?.thresholdAscendingArcArea ?? 0 },
                        { d: result.distributions.deltaMaxImaginaryInterpolated, label: `ΔMax Imaginary Interpolated (${SIGNAL_METHOD_UNITS.MaxImaginaryInterpolated})`, bound: thresholds?.thresholdMaxImaginaryInterpolated ?? 0 },
                        { d: result.distributions.deltaMaxImaginaryAbsolute, label: `ΔMax Imaginary Absolute (${SIGNAL_METHOD_UNITS.MaxImaginaryAbsolute})`, bound: thresholds?.thresholdMaxImaginaryAbsolute ?? 0 },
                      ].map(({ d, label, bound }) => (
                        <Box key={label} sx={{ minWidth: 220, flex: 1 }}>
                          <MetricHistogram label={label} mean={d.mean} min={d.min} max={d.max} boundsMin={0} boundsMax={bound} rawValues={d.rawValues} />
                        </Box>
                      ))}
                    </Stack>
                  </Paper>

                  {/* Apply Selected */}
                  {!resultMatchesChannel && (
                    <Alert severity="warning">
                      These results were calculated for CH{result.channelId}, not CH{channel}. Run Analyze again for CH{channel} before applying.
                    </Alert>
                  )}
                  {appliedNotice && (
                    <Alert severity="info" onClose={() => setAppliedNotice(false)}>
                      Changes applied to the CH{channel} Active Configuration above. Click <strong>Save CH{channel} Configuration</strong> to persist.
                    </Alert>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Tooltip title={!anyRowChecked ? 'Select at least one parameter to apply' : ''}>
                      <span>
                        <Button
                          variant="contained"
                          color="secondary"
                          disabled={!anyRowChecked || !resultMatchesChannel}
                          onClick={handleApplySelected}
                        >
                          Apply to CH{channel} configuration
                        </Button>
                      </span>
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary">
                      Copies checked values to the CH{channel} Active Configuration. Save to persist.
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Collapse>
        </Paper>

      <Dialog open={discardPromptOpen} onClose={() => setDiscardPromptOpen(false)}>
        <DialogTitle>Discard unsaved changes to CH{pendingSwitch?.from}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The CH{pendingSwitch?.from} configuration has changes that have not been saved.
            Switching to CH{pendingSwitch?.to} discards them.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardPromptOpen(false)}>Keep editing</Button>
          <Button color="warning" onClick={() => pendingSwitch && switchChannel(pendingSwitch.to)}>
            Discard and switch to CH{pendingSwitch?.to}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={successMsg !== null}
        autoHideDuration={8000}
        onClose={() => setSuccessMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMsg(null)}>{successMsg}</Alert>
      </Snackbar>
    </Box>
  );
}
